import React from 'react';
import { render, fireEvent } from '@testing-library/react';
import Sermons from '../Sermons';

const sampleSeries = [
  {
    id: 1,
    title: 'Grace Abounding',
    description: 'A series on grace',
    artwork_url: 'https://example.com/grace.jpg',
    episode_count: 3,
  },
  {
    id: 2,
    title: 'Faith Forward',
    description: 'Walking in faith and confidence through every season of life and beyond that too',
    artwork_url: null,
    episode_count: 2,
  },
];

const sampleEpisodes = [
  { id: 10, title: 'Grace Part 1', speaker: 'Pastor John', published_at: '2025-01-15', series_id: 1 },
  { id: 11, title: 'Grace Part 2', speaker: null, published_at: '2025-01-22', series_id: 1 },
  { id: 12, title: 'Faith Intro', speaker: 'Pastor Jane', published_at: '2025-02-01', series_id: 2 },
];

describe('Sermons', () => {
  describe('empty state', () => {
    it('shows empty message when no data', () => {
      const { getByText } = render(<Sermons data={null} />);
      expect(getByText('No series available at this time.')).toBeTruthy();
    });

    it('shows empty message for empty arrays', () => {
      const { getByText } = render(<Sermons data={{ series: [], episodes: [] }} />);
      expect(getByText('No series available at this time.')).toBeTruthy();
    });
  });

  describe('latest-series display (default)', () => {
    it('renders the first series as hero', () => {
      const { getByText, getByAltText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} />
      );
      expect(getByText('Grace Abounding')).toBeTruthy();
      expect(getByText('A series on grace')).toBeTruthy();
      expect(getByAltText('Grace Abounding')).toBeTruthy();
    });

    it('renders episodes belonging to the latest series', () => {
      const { getByText, queryByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} />
      );
      expect(getByText('Grace Part 1')).toBeTruthy();
      expect(getByText('Grace Part 2')).toBeTruthy();
      expect(queryByText('Faith Intro')).toBeNull();
    });

    it('shows episode speaker and date', () => {
      const { getByText, container } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} />
      );
      expect(getByText(/Pastor John/)).toBeTruthy();
      // Date renders via toLocaleDateString; both episodes have Jan dates
      const dateMetas = container.querySelectorAll('.pco-ep-meta');
      expect(dateMetas.length).toBe(2);
      expect(dateMetas[0].textContent).toMatch(/Jan \d+, 2025/);
    });

    it('calls onItemClick when episode is clicked', () => {
      const handleClick = jest.fn();
      const { getByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} onItemClick={handleClick} />
      );
      fireEvent.click(getByText('Grace Part 1').closest('.pco-episode-clickable'));
      expect(handleClick).toHaveBeenCalledWith(10);
    });

    it('does not call onItemClick in studioMode', () => {
      const handleClick = jest.fn();
      const { container } = render(
        <Sermons
          data={{ series: sampleSeries, episodes: sampleEpisodes }}
          onItemClick={handleClick}
          settings={{ _studioMode: true }}
        />
      );
      const item = container.querySelector('.pco-episode-item');
      fireEvent.click(item);
      expect(handleClick).not.toHaveBeenCalled();
    });

    it('shows episode_count from series when available', () => {
      const { getByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} />
      );
      expect(getByText('3 messages')).toBeTruthy();
    });

    it('shows empty message when series array is empty', () => {
      const { getByText } = render(
        <Sermons data={{ series: [], episodes: sampleEpisodes }} settings={{ 'pco-display': 'latest-series' }} />
      );
      expect(getByText('No series available.')).toBeTruthy();
    });
  });

  describe('all-series display', () => {
    it('renders all series as cards', () => {
      const { getByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} settings={{ 'pco-display': 'all-series' }} />
      );
      expect(getByText('Grace Abounding')).toBeTruthy();
      expect(getByText('Faith Forward')).toBeTruthy();
    });

    it('truncates long descriptions to 120 chars', () => {
      const { container } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} settings={{ 'pco-display': 'all-series' }} />
      );
      const cards = container.querySelectorAll('.pco-series-card');
      expect(cards.length).toBe(2);
    });

    it('shows empty message when no series', () => {
      const { getByText } = render(
        <Sermons data={{ series: [], episodes: [] }} settings={{ 'pco-display': 'all-series' }} />
      );
      expect(getByText('No series available at this time.')).toBeTruthy();
    });
  });

  describe('recent-messages display', () => {
    it('renders episodes with series title in speaker', () => {
      const { getByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: sampleEpisodes }} settings={{ 'pco-display': 'recent-messages' }} />
      );
      expect(getByText('Grace Part 1')).toBeTruthy();
      expect(getByText('Faith Intro')).toBeTruthy();
    });

    it('shows empty message when no episodes', () => {
      const { getByText } = render(
        <Sermons data={{ series: sampleSeries, episodes: [] }} settings={{ 'pco-display': 'recent-messages' }} />
      );
      expect(getByText('No messages available.')).toBeTruthy();
    });
  });

  describe('Array data format', () => {
    it('accepts data as array of series with nested episodes', () => {
      const arrayData = [
        { ...sampleSeries[0], episodes: [sampleEpisodes[0], sampleEpisodes[1]] },
        { ...sampleSeries[1], episodes: [sampleEpisodes[2]] },
      ];
      const { getByText } = render(<Sermons data={arrayData} />);
      expect(getByText('Grace Abounding')).toBeTruthy();
      expect(getByText('Grace Part 1')).toBeTruthy();
    });
  });
});
